import type { InstagramStatus } from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ApiError, api, post } from "../api";
import { Steps } from "../components/Steps";

const errors: Record<string, { title: string; help: string[] }> = {
  no_business_account: {
    title: "We couldn't find an Instagram business account",
    help: [
      "In the Instagram app, go to Settings, then Account type and tools, and switch to a professional account (Business or Creator).",
      "Link it to a Facebook Page: in Instagram, go to Edit profile, then Page, and pick or create one.",
      "Then come back and connect again. When Facebook asks, tick both the Page and the Instagram account.",
    ],
  },
  cancelled: {
    title: "Connecting was cancelled",
    help: ["Nothing was changed. Connect again when you're ready."],
  },
  wrong_user: {
    title: "You were logged out while connecting",
    help: ["Log in again, then connect Instagram."],
  },
  failed: {
    title: "Instagram didn't let us connect",
    help: [
      "Please try again. If it keeps happening, check that you are an admin of the Facebook Page.",
    ],
  },
};

export function Connect() {
  const { ws } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const status = useQuery({
    queryKey: ["instagram", ws],
    queryFn: () => api<InstagramStatus>(`/v1/w/${ws}/instagram`),
  });
  const choose = params.get("choose");
  const choices = useQuery({
    queryKey: ["ig-choices", choose],
    queryFn: () =>
      api<{
        accounts: {
          igUserId: string;
          username: string;
          pageName: string;
          picture: string | null;
        }[];
      }>(`/v1/instagram/choices/${choose}`),
    enabled: Boolean(choose),
  });
  const problem = errors[params.get("error") ?? ""];

  const connect = async () => {
    setError(null);
    try {
      const { url } = await post<{ url: string }>(`/v1/w/${ws}/instagram/connect`);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  };
  const pick = async (igUserId: string) => {
    await post("/v1/instagram/choose", { state: choose, igUserId });
    await qc.invalidateQueries({ queryKey: ["instagram", ws] });
    navigate(`/w/${ws}/connect?connected=1`, { replace: true });
  };

  const s = status.data;
  return (
    <div className="onb">
      <div className="box">
        <img className="logo" src="/logo.svg" alt="Brandwall" />
        <Steps at={s?.connected && s.status === "connected" ? 3 : 2} />
        <div className="card stack">
          <div>
            <h1>Connect Instagram</h1>
            <p className="muted">
              Connect your brand's Instagram account, so Brandwall can collect the posts people
              share about you.
            </p>
          </div>
          {problem && (
            <div className="error" role="alert">
              <b>{problem.title}</b>
              <ol className="steps-list" style={{ marginTop: 6 }}>
                {problem.help.map((h) => (
                  <li key={h}>{h}</li>
                ))}
              </ol>
            </div>
          )}
          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}
          {choose && choices.data && (
            <div className="stack">
              <h3>Which Instagram account is this brand's?</h3>
              {choices.data.accounts.map((a) => (
                <button
                  key={a.igUserId}
                  type="button"
                  className="btn secondary block"
                  onClick={() => pick(a.igUserId)}
                >
                  @{a.username} <span className="muted small">({a.pageName})</span>
                </button>
              ))}
            </div>
          )}
          {s?.connected && s.status === "connected" ? (
            <>
              <div className="row">
                {s.profilePictureUrl && (
                  <img
                    src={s.profilePictureUrl}
                    alt=""
                    width={44}
                    height={44}
                    style={{ borderRadius: "50%" }}
                  />
                )}
                <div>
                  <b>@{s.username}</b>
                  <div className="small muted">Business account</div>
                </div>
                <span className="spacer" />
                <span className="pill ok">
                  <span className="dot" /> Connected
                </span>
              </div>
              <button
                type="button"
                className="btn block"
                onClick={() => navigate(`/w/${ws}/collecting`)}
              >
                Continue
              </button>
              <button type="button" className="btn link small" onClick={connect}>
                Connect a different account
              </button>
            </>
          ) : (
            !choose && (
              <>
                <div className="stack small" style={{ gap: 8 }}>
                  <p>
                    <b>Brandwall collects</b>
                  </p>
                  <p>✓ Posts that tag or mention you</p>
                  <p>✓ Posts with hashtags you choose</p>
                  <p>✓ Your own posts</p>
                  <p className="muted">
                    Brandwall never posts or sends messages for you. It only comments to ask for
                    permission to show a post, and you can turn that off.
                  </p>
                </div>
                <p className="small muted">
                  You need an Instagram business or creator account linked to a Facebook Page.
                  Facebook will ask you to choose both.
                </p>
                <button type="button" className="btn block" onClick={connect}>
                  Connect Instagram →
                </button>
                {s?.status === "broken" && (
                  <p className="small error">
                    Instagram stopped sending us your posts. Connect again to fix it.
                  </p>
                )}
              </>
            )
          )}
          <Link to={`/w/${ws}`} className="small muted" style={{ textAlign: "center" }}>
            Skip for now
          </Link>
        </div>
      </div>
    </div>
  );
}
